/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import React from 'react'

import { FormattedMessage } from '@modules/i18n'
import type {
  SecurityProductLine,
  SecurityProductTier,
  SecurityProductType,
} from '@modules/project-user-api/v1/types'

import type { ReactNode } from 'react'

export type ProductDefinition = {
  obj: SecurityProductType
  title: ReactNode
  preamble?: ReactNode
  bullets: ReactNode[]
}

export type ProductTypesMap = {
  complete: {
    security: ProductDefinition
    endpoint: ProductDefinition
    cloud: ProductDefinition
  }
  essentials: {
    security: ProductDefinition
    endpoint: ProductDefinition
    cloud: ProductDefinition
  }
  search_ai_lake: {
    ai_soc: ProductDefinition
  }
}

export const PRODUCT_TYPES: ProductTypesMap = {
  essentials: {
    security: {
      obj: {
        product_line: 'security',
        product_tier: 'essentials',
      },
      title: (
        <FormattedMessage
          id='projectSecurityFeatures.security.essentials.title'
          defaultMessage='Security Analytics Essentials'
        />
      ),
      preamble: (
        <FormattedMessage
          id='projectSecurityFeatures.security.essentials.preamble'
          defaultMessage='Everything you need to operationalize traditional SIEM in most organizations.'
        />
      ),
      bullets: [
        <FormattedMessage
          id='projectSecurityFeatures.security.essentials.bullets.1'
          defaultMessage='Ad hoc analytics, machine learning'
        />,
        <FormattedMessage
          id='projectSecurityFeatures.security.essentials.bullets.2'
          defaultMessage='Prebuilt detection rules'
        />,
        <FormattedMessage
          id='projectSecurityFeatures.security.essentials.bullets.3'
          defaultMessage='Triage, investigation, and hunting'
        />,
        <FormattedMessage
          id='projectSecurityFeatures.security.essentials.bullets.4'
          defaultMessage='Collaboration workflows'
        />,
        <FormattedMessage
          id='projectSecurityFeatures.security.essentials.bullets.5'
          defaultMessage='Threat intelligence integration'
        />,
      ],
    },
    endpoint: {
      obj: {
        product_line: 'endpoint',
        product_tier: 'essentials',
      },
      title: (
        <FormattedMessage
          id='projectSecurityFeatures.endpoint.essentials.title'
          defaultMessage='Endpoint Protection Essentials'
        />
      ),
      bullets: [
        <FormattedMessage
          id='projectSecurityFeatures.endpoint.essentials.bullets.1'
          defaultMessage='Elastic Defend for malware prevention'
        />,
        <FormattedMessage
          id='projectSecurityFeatures.endpoint.essentials.bullets.2'
          defaultMessage='Ransomware prevention'
        />,
        <FormattedMessage
          id='projectSecurityFeatures.endpoint.essentials.bullets.3'
          defaultMessage='Memory and behavior prevention'
        />,
      ],
    },
    cloud: {
      obj: {
        product_line: 'cloud',
        product_tier: 'essentials',
      },
      title: (
        <FormattedMessage
          id='projectSecurityFeatures.cloud.essentials.title'
          defaultMessage='Cloud Protection Essentials'
        />
      ),
      bullets: [
        <FormattedMessage
          id='projectSecurityFeatures.cloud.essentials.bullets.1'
          defaultMessage='Workload runtime protection'
        />,
        <FormattedMessage
          id='projectSecurityFeatures.cloud.essentials.bullets.2'
          defaultMessage='Cloud Native Posture Management for: Kubernetes | AWS | GCP | More'
        />,
      ],
    },
  },
  complete: {
    security: {
      obj: {
        product_line: 'security',
        product_tier: 'complete',
      },
      title: (
        <FormattedMessage
          id='projectSecurityFeatures.security.complete.title'
          defaultMessage='Security Analytics Complete'
        />
      ),
      preamble: (
        <FormattedMessage
          id='projectSecurityFeatures.security.complete.preamble'
          defaultMessage='Everything in Security Analytics Essentials plus:'
        />
      ),
      bullets: [
        <FormattedMessage
          id='projectSecurityFeatures.security.complete.bullets.1'
          defaultMessage='Everything in "Essential"'
        />,
        <FormattedMessage
          id='projectSecurityFeatures.security.complete.bullets.2'
          defaultMessage='UEBA and TIP'
        />,
        <FormattedMessage
          id='projectSecurityFeatures.security.complete.bullets.3'
          defaultMessage='Bidirectional webhooks'
        />,
        <FormattedMessage
          id='projectSecurityFeatures.security.complete.bullets.4'
          defaultMessage='Security content'
        />,
        <FormattedMessage
          id='projectSecurityFeatures.security.complete.bullets.5'
          defaultMessage='Elastic AI Assistant'
        />,
      ],
    },
    endpoint: {
      obj: {
        product_line: 'endpoint',
        product_tier: 'complete',
      },
      title: (
        <FormattedMessage
          id='projectSecurityFeatures.endpoint.complete.title'
          defaultMessage='Endpoint Protection Complete'
        />
      ),
      bullets: [
        <FormattedMessage
          id='projectSecurityFeatures.endpoint.complete.bullets.1'
          defaultMessage='Everything in "Essential"'
        />,
        <FormattedMessage
          id='projectSecurityFeatures.endpoint.complete.bullets.2'
          defaultMessage='Endpoint response actions'
        />,
        <FormattedMessage
          id='projectSecurityFeatures.endpoint.complete.bullets.3'
          defaultMessage='Advanced endpoint policy management'
        />,
      ],
    },
    cloud: {
      obj: {
        product_line: 'cloud',
        product_tier: 'complete',
      },
      title: (
        <FormattedMessage
          id='projectSecurityFeatures.cloud.complete.title'
          defaultMessage='Cloud Protection Complete'
        />
      ),
      bullets: [
        <FormattedMessage
          id='projectSecurityFeatures.cloud.complete.bullets.1'
          defaultMessage='Everything in "Essential"'
        />,
        <FormattedMessage
          id='projectSecurityFeatures.cloud.complete.bullets.2'
          defaultMessage='Drift protection for containers'
        />,
        <FormattedMessage
          id='projectSecurityFeatures.cloud.complete.bullets.3'
          defaultMessage='Response actions'
        />,
      ],
    },
  },
  search_ai_lake: {
    ai_soc: {
      obj: {
        product_line: 'ai_soc',
        product_tier: 'search_ai_lake',
      },
      title: (
        <FormattedMessage
          id='projectSecurityFeatures.search_ai_lake.ai_soc.title'
          defaultMessage='AI for the SOC'
        />
      ),
      preamble: (
        <FormattedMessage
          id='projectSecurityFeatures.search_ai_lake.ai_soc.preamble'
          defaultMessage='Elastic AI capabilities to power your SIEM operations'
        />
      ),
      bullets: [
        <FormattedMessage
          id='projectSecurityFeatures.search_ai_lake.ai_soc.bullets.1'
          defaultMessage='AI insights with Attack Discovery'
        />,
        <FormattedMessage
          id='projectSecurityFeatures.search_ai_lake.ai_soc.bullets.2'
          defaultMessage='AI Assistant with custom knowledge support'
        />,
        <FormattedMessage
          id='projectSecurityFeatures.search_ai_lake.ai_soc.bullets.3'
          defaultMessage='Cases to collect and share information'
        />,
        <FormattedMessage
          id='projectSecurityFeatures.search_ai_lake.ai_soc.bullets.4'
          defaultMessage='Native integrations with third-party SIEM and EDR platforms'
        />,
      ],
    },
  },
}

export const getProductDefinition = <T extends SecurityProductTier>(
  tier: T,
  line: SecurityProductLine,
): ProductDefinition => PRODUCT_TYPES[tier][line]

export const DEFAULT_VALUE = Object.values(PRODUCT_TYPES.complete).map(({ obj }) => obj)
